import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const PDF_PAGE_WIDTH_MM = 215.9;
const PDF_MARGIN_MM = 8;
const MAX_PDF_PAGE_HEIGHT_MM = 5000;
// Stay below common browser canvas dimension and pixel-area ceilings. Very
// large reports fail with an actionable error instead of risking truncation.
const MAX_CANVAS_DIMENSION = 16000;
const MAX_CANVAS_PIXELS = 16000000;

function assertSafeCaptureSize(width, height) {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0 ||
    width > MAX_CANVAS_DIMENSION ||
    height > MAX_CANVAS_DIMENSION ||
    width * height > MAX_CANVAS_PIXELS
  ) {
    throw new Error(
      "Dashboard is too large to capture safely for PDF export."
    );
  }
}

function getSinglePageDimensions(canvas) {
  const contentWidthMm = PDF_PAGE_WIDTH_MM - PDF_MARGIN_MM * 2;
  const imageHeightMm = (canvas.height / canvas.width) * contentWidthMm;
  const pageHeightMm = imageHeightMm + PDF_MARGIN_MM * 2;

  if (
    !Number.isFinite(imageHeightMm) ||
    !Number.isFinite(pageHeightMm) ||
    pageHeightMm > MAX_PDF_PAGE_HEIGHT_MM
  ) {
    throw new Error(
      "Dashboard is too tall for a supported single-page PDF export."
    );
  }

  return { contentWidthMm, imageHeightMm, pageHeightMm };
}

function safeFilenamePart(value) {
  const cleaned = String(value || "")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

  return cleaned || "Dashboard";
}

function formatFileDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function afterNextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

async function rasterizeLogo(element) {
  const logo = element.querySelector('img[alt="Toren"]');

  if (!(logo instanceof HTMLImageElement)) {
    return null;
  }

  const source = logo.currentSrc || logo.src;

  if (!source) {
    return null;
  }

  try {
    const sourceImage = new Image();

    await new Promise((resolve, reject) => {
      sourceImage.onload = resolve;
      sourceImage.onerror = reject;
      sourceImage.src = source;
    });

    const rootRect = element.getBoundingClientRect();
    const rect = logo.getBoundingClientRect();

    const width = Math.max(
      1,
      Math.round(rect.width * 3)
    );

    const height = Math.max(
      1,
      Math.round(rect.height * 3)
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");

    if (!context) {
      return null;
    }

    context.clearRect(0, 0, width, height);

    context.drawImage(
      sourceImage,
      0,
      0,
      width,
      height
    );

    return {
      dataUrl: canvas.toDataURL("image/png"),
      left: rect.left - rootRect.left,
      top: rect.top - rootRect.top,
      width: rect.width,
      height: rect.height,
      rootWidth: rootRect.width
    };
  } catch (error) {
    console.warn(
      "[PDF Export] Could not rasterize Toren logo:",
      error
    );

    return null;
  }
}

export async function exportDashboardPdf({
  element,
  accountName,
  fileDate = new Date()
}) {
  if (!element) {
    throw new Error(
      "Dashboard export element is required."
    );
  }

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  await afterNextPaint();

  const rasterLogo = await rasterizeLogo(element);
  const captureScale = Math.min(
    1.5,
    Math.max(1, window.devicePixelRatio || 1)
  );
  const elementRect = element.getBoundingClientRect();
  const estimatedCssWidth = Math.max(
    elementRect.width || 0,
    element.scrollWidth || 0
  );
  const estimatedCssHeight = Math.max(
    elementRect.height || 0,
    element.scrollHeight || 0
  );

  if (estimatedCssWidth > 0 && estimatedCssHeight > 0) {
    const estimatedCanvasWidth = Math.ceil(estimatedCssWidth * captureScale);
    const estimatedCanvasHeight = Math.ceil(estimatedCssHeight * captureScale);
    assertSafeCaptureSize(estimatedCanvasWidth, estimatedCanvasHeight);
    getSinglePageDimensions({
      width: estimatedCanvasWidth,
      height: estimatedCanvasHeight
    });
  }

  const canvas = await html2canvas(element, {
    backgroundColor: "#f8fafc",
    scale: captureScale,

    useCORS: true,
    logging: false,

    ignoreElements: (node) =>
      Boolean(node.classList?.contains("no-print")),

    onclone: (clonedDocument) => {
      // Remove interaction-only controls from the cloned page itself. This
      // keeps the export static even when html2canvas's ignore callback runs
      // before or independently of clone mutations.
      clonedDocument.querySelectorAll(".no-print").forEach((node) => node.remove());

      clonedDocument.querySelectorAll("select[data-pdf-static-label]").forEach((select) => {
        const selectedOption = select.selectedOptions?.[0];
        const staticValue = clonedDocument.createElement("span");
        staticValue.className = "pdf-static-value";
        staticValue.textContent = selectedOption?.textContent?.trim() || "Unavailable";
        select.replaceWith(staticValue);
      });

      clonedDocument.querySelectorAll('[role="tablist"]').forEach((tablist) => {
        const selectedTab = tablist.querySelector('[role="tab"][aria-selected="true"]');
        const tabName = selectedTab?.textContent?.trim();
        const groupName = tablist.getAttribute("aria-label")?.trim();
        if (!tabName) {
          tablist.remove();
          return;
        }

        const staticLabel = clonedDocument.createElement("p");
        staticLabel.className = "pdf-static-section-label";
        staticLabel.textContent = groupName ? `${groupName}: ${tabName}` : tabName;
        tablist.replaceWith(staticLabel);
      });

      clonedDocument.querySelectorAll('[data-pdf-control-group="leaderboard-view"]').forEach((group) => {
        const selectedControl = group.querySelector('button[aria-pressed="true"]');
        const selectedLabel = selectedControl?.textContent?.trim();
        const staticLabel = clonedDocument.createElement("p");
        staticLabel.className = "pdf-static-section-label";
        staticLabel.textContent = selectedLabel ? `Existing Local AI Leaderboard view: ${selectedLabel}` : "Existing Local AI Leaderboard";
        group.replaceWith(staticLabel);
      });

      clonedDocument
        .querySelectorAll("details:not([open])")
        .forEach((details) => {
          details.style.display = "none";
        });

      /*
       * Don't let html2canvas attempt the SVG logo.
       * We'll place a clean rasterized logo directly
       * into the finished PDF afterward.
       */
      const clonedLogo =
        clonedDocument.querySelector(
          'img[alt="Toren"]'
        );

      if (clonedLogo instanceof HTMLImageElement) {
        clonedLogo.style.visibility = "hidden";
      }

      clonedDocument
        .querySelectorAll("button")
        .forEach((button) => {
          button.remove();
        });

    }
  });

  if (!canvas.width || !canvas.height) {
    throw new Error(
      "Dashboard capture returned an empty canvas."
    );
  }

  assertSafeCaptureSize(canvas.width, canvas.height);
  const { contentWidthMm, imageHeightMm, pageHeightMm } =
    getSinglePageDimensions(canvas);
  const dashboardImageData = canvas.toDataURL("image/png");

  if (!dashboardImageData || dashboardImageData === "data:,") {
    throw new Error(
      "Dashboard capture could not be encoded for PDF export."
    );
  }

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [PDF_PAGE_WIDTH_MM, pageHeightMm],
    compress: true
  });
  pdf.addImage(
    dashboardImageData,
    "PNG",
    PDF_MARGIN_MM,
    PDF_MARGIN_MM,
    contentWidthMm,
    imageHeightMm,
    undefined,
    "FAST"
  );

  /*
   * Overlay the Toren logo directly into the PDF.
   * This avoids html2canvas's SVG rendering issue.
   */
  if (rasterLogo) {
    const cssToPdfScale =
      contentWidthMm / rasterLogo.rootWidth;

    pdf.addImage(
      rasterLogo.dataUrl,
      "PNG",
      PDF_MARGIN_MM +
        rasterLogo.left * cssToPdfScale,
      PDF_MARGIN_MM +
        rasterLogo.top * cssToPdfScale,
      rasterLogo.width * cssToPdfScale,
      rasterLogo.height * cssToPdfScale,
      undefined,
      "FAST"
    );
  }

  const filename =
    `Toren-${safeFilenamePart(
      accountName || "Dashboard"
    )}-` +
    `${formatFileDate(fileDate)}.pdf`;

  pdf.save(filename);
}
